import { useState, useEffect } from 'react';
import useApi from '../../api';
import { conLog } from '../../utils/conLog';
import { toast } from 'react-toastify';
import toastConfig from '../../config/Toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Globe, Search, Clock } from 'lucide-react';
import { formatRelativeTime } from '../../utils/dateFormatter';
import { ScrollArea } from '@/components/ui/scroll-area';

export default function SelectWebsites({ agentId, setShowWebsites, setWebsitesSelected }: { agentId: string | undefined, setShowWebsites: any, setWebsitesSelected: any }) {
  const [isOpen, setIsOpen] = useState(true);
  const [websites, setWebsites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedWebsites, setSelectedWebsites] = useState<Set<string>>(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [associating, setAssociating] = useState(false);
  const { get, post } = useApi();

  function closeModal() {
    setIsOpen(false);
    setShowWebsites(false);
  }

  function toggleWebsiteSelection(websiteId: string) {
    setSelectedWebsites((prev) => {
      const updated = new Set(prev);
      if (updated.has(websiteId)) {
        updated.delete(websiteId);
      } else {
        updated.add(websiteId);
      }
      return updated;
    });
  }

  function associateWebsitesWithAgent() {
    const selectedWebsitesArray = Array.from(selectedWebsites);
    setAssociating(true);

    post(`/agents/${agentId}/websites`, { website_ids: selectedWebsitesArray })
      .then((response) => {
        setWebsitesSelected(true);
        setShowWebsites(false);
        closeModal();
        toast.success('Websites associated successfully', toastConfig);
        return response.data;
      })
      .catch((error) => {
        console.error('Error associating websites:', error);
        toast.error('Error associating websites', toastConfig);
      })
      .finally(() => setAssociating(false));
  }

  useEffect(() => {
    if (!isOpen) return;

    const url = agentId ? `/websites/?exclude_agent_id=${agentId}` : '/websites/';

    get(url)
      .then((response) => {
        conLog(response.data);
        setWebsites(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching websites:', error);
        setLoading(false);
      });
  }, [isOpen, agentId]);

  const filteredWebsites = websites.filter((website: any) =>
    website.url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && closeModal()}>
      <DialogContent className="sm:max-w-lg overflow-hidden">
        <DialogHeader>
            <div>
              <DialogTitle>Add existing website</DialogTitle>
              <DialogDescription className="mt-0.5">
                Select websites from your global knowledge to associate with this agent.
              </DialogDescription>
            </div>
        </DialogHeader>

        <div className="space-y-3 min-w-0">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search websites..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {selectedWebsites.size > 0 && (
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">
                {selectedWebsites.size} selected
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-auto px-0 py-0 text-xs font-normal text-muted-foreground hover:bg-transparent hover:text-foreground"
                onClick={() => setSelectedWebsites(new Set())}
              >
                Clear selection
              </Button>
            </div>
          )}

          <ScrollArea className="h-[320px] rounded-lg border">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin h-5 w-5 border-2 border-muted-foreground rounded-full border-t-transparent"></div>
                <p className="text-sm text-muted-foreground mt-3">Loading websites...</p>
              </div>
            ) : filteredWebsites.length > 0 ? (
              <div className="divide-y">
                {filteredWebsites.map((website: any) => (
                  <label
                    key={website.id}
                    htmlFor={`ws-${website.id}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
                  >
                    <Checkbox
                      id={`ws-${website.id}`}
                      checked={selectedWebsites.has(website.id)}
                      onCheckedChange={() => toggleWebsiteSelection(website.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{website.url}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {website.page_count > 0 && (
                          <span className="text-xs text-muted-foreground">
                            {website.page_count} pages
                          </span>
                        )}
                        {website.indexed_at && (
                          <span className="text-xs text-muted-foreground flex items-center">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatRelativeTime(website.indexed_at)}
                          </span>
                        )}
                      </div>
                    </div>
                    {website.index_status === 'Completed' && (
                      <span className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                        Trained
                      </span>
                    )}
                  </label>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                  <Globe className="h-5 w-5 text-muted-foreground" />
                </div>
                <p className="text-sm font-medium text-muted-foreground mt-3">
                  {searchTerm ? 'No matching websites' : 'No websites available'}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {searchTerm ? 'Try a different search term.' : 'Train a website first, then you can share it across agents.'}
                </p>
              </div>
            )}
          </ScrollArea>
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={closeModal}>
            Cancel
          </Button>
          <Button
            disabled={selectedWebsites.size === 0 || associating}
            onClick={associateWebsitesWithAgent}
          >
            {associating ? (
              <div className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Adding...
              </div>
            ) : (
              selectedWebsites.size > 0
                ? `Add ${selectedWebsites.size} website${selectedWebsites.size !== 1 ? 's' : ''}`
                : 'Add websites'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
